import { useQuery } from '@tanstack/react-query';
import { Card } from '@/components/ui/card';
import { apiClient } from '@/lib/api-client';
import { AdminShell } from './admin-shell';
import { ScrollText } from 'lucide-react';

interface AuditEntry {
  auditId: string;
  actorUserId: string;
  actorEmail: string | null;
  action: string;
  targetType: string;
  targetId: string;
  summary: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  ip: string | null;
  userAgent: string | null;
  createdAt: string;
}

export function AdminAuditLogsPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'audit-logs'],
    queryFn: () => apiClient.adminAuditLogs(),
  });
  const rows = (data?.data as AuditEntry[] | undefined) ?? [];

  return (
    <AdminShell subtitle="Every administrative action recorded, oldest cursor scrolls down.">

      <div className="flex items-center gap-3">
        <ScrollText className="h-5 w-5 text-muted-foreground" />
        <h1 className="text-2xl font-bold text-foreground">Audit log</h1>
        <span className="text-sm text-muted-foreground">({rows.length} most recent)</span>
      </div>

      <Card className="p-0 overflow-hidden">
        <div className="divide-y divide-border">
          {isLoading && (
            <div className="px-5 py-8 text-center text-sm text-muted-foreground">Loading…</div>
          )}
          {!isLoading && rows.length === 0 && (
            <div className="px-5 py-8 text-center text-sm text-muted-foreground">No admin actions recorded yet.</div>
          )}
          {rows.map((row) => (
            <div key={row.auditId} className="px-5 py-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <p className="text-sm flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{row.action}</span>
                    <span className="text-foreground">{row.summary ?? `${row.targetType}/${row.targetId}`}</span>
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    by <strong className="text-foreground">{row.actorEmail ?? row.actorUserId}</strong>
                    {row.ip && <span> · {row.ip}</span>}
                  </p>
                </div>
                <span className="text-xs text-muted-foreground shrink-0">{new Date(row.createdAt).toLocaleString()}</span>
              </div>
              {(row.before || row.after) && (
                <details className="mt-2">
                  <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">Show before/after</summary>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                    <pre className="text-[11px] bg-muted/40 rounded p-2 overflow-x-auto">{JSON.stringify(row.before ?? null, null, 2)}</pre>
                    <pre className="text-[11px] bg-muted/40 rounded p-2 overflow-x-auto">{JSON.stringify(row.after ?? null, null, 2)}</pre>
                  </div>
                </details>
              )}
            </div>
          ))}
        </div>
      </Card>
    </AdminShell>
  );
}
